import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, CheckCircle2, CircleHelp, Landmark, Maximize, Minimize, RefreshCw, TriangleAlert, WalletCards } from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Modal, Spinner } from "../components/ui";
import type { PublicWalletTransfer, WalletReturnAllResult } from "../wallet-types";
import { clearGameExitReturnIntent, createGameExitReturnIntent, persistGameExitReturnIntent, readGameExitReturnIntent, type GameExitReturnIntent } from "./game-exit-return";
import { useMobileNavigationGuard } from "./shared";
import { isDurableWalletReview, walletSettlementSummary } from "./wallet-intent";
import type { LobbyGame } from "./types";
import "./game-player.css";

type GamePlayerProps = {
  game: Pick<LobbyGame, "name" | "provider" | "gameType" | "gameCode">;
  status: "connecting" | "ready" | "error";
  owner: string;
  url?: string;
  error?: string;
  onClose: () => void;
  onRetry: () => void;
  onSessionExpired: () => void;
  onBalanceReturned: () => void;
  browserExitRequested?: boolean;
  onBrowserExitCancel?: () => void;
};

type ExitPhase = "choice" | "busy" | "success" | "empty" | "failed" | "blocked" | "review" | "unknown" | "expired";
type ExitDialogState = {
  phase: ExitPhase;
  intent?: GameExitReturnIntent;
  message?: string;
  amountCents?: number;
  actualAmountCents?: number;
  transferId?: string;
};

const exitMoney = new Intl.NumberFormat("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function validTransfer(value: unknown, owner: string, provider: string, amountCents: number): value is PublicWalletTransfer {
  if (!value || typeof value !== "object") return false;
  const transfer = value as Partial<PublicWalletTransfer>;
  return typeof transfer.id === "string" && transfer.id.length > 0
    && transfer.userId === owner
    && transfer.provider === provider
    && transfer.direction === "withdrawal"
    && transfer.amountCents === amountCents
    && transfer.requestKind === "exit_return_all"
    && typeof transfer.status === "string"
    && ["submitting", "pending", "succeeded", "failed"].includes(transfer.status);
}

function validReturnResult(value: unknown, owner: string, provider: string): value is WalletReturnAllResult {
  if (!value || typeof value !== "object") return false;
  const result = value as Partial<WalletReturnAllResult>;
  if (result.provider !== provider || !Number.isSafeInteger(result.amountCents) || (result.amountCents ?? -1) < 0 || typeof result.message !== "string") return false;
  if (result.result === "empty") return result.amountCents === 0 && !result.transfer;
  return result.result === "submitted" && (result.amountCents ?? 0) > 0 && validTransfer(result.transfer, owner, provider, result.amountCents!);
}

export function GamePlayer({ game, status, owner, url, error, onClose, onRetry, onSessionExpired, onBalanceReturned, browserExitRequested = false, onBrowserExitCancel }: GamePlayerProps) {
  const [loadedUrl, setLoadedUrl] = useState<string>();
  const [slow, setSlow] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mode, setMode] = useState<"normal" | "fullscreen" | "immersive">("normal");
  const [fullBusy, setFullBusy] = useState(false);
  const [fullError, setFullError] = useState("");
  const [exitDialog, setExitDialog] = useState<ExitDialogState | null>(() => {
    const saved = readGameExitReturnIntent(owner, game.provider);
    return saved ? { phase: "unknown", intent: saved, message: "上次退出时的转回结果尚未确认。请沿用原请求核对，避免重复转账。" } : null;
  });
  const playerRef = useRef<HTMLDivElement>(null), mounted = useRef(true), fullLock = useRef(false);
  const exitRequest = useRef<AbortController | null>(null);
  const exitRequestVersion = useRef(0);
  const helpTriggerRef = useRef<HTMLButtonElement>(null);
  const exitPanelRef = useRef<HTMLElement>(null);
  const exitFocusRef = useRef<HTMLButtonElement>(null);
  const exitTriggerRef = useRef<HTMLElement | null>(null);
  const helpId = useId();
  const frameLoaded = Boolean(url && loadedUrl === url);
  const hasFrame = status === "ready" && Boolean(url);
  // CQ9's verified electronic lobby uses noopener _blank links for its games.
  // Child windows inherit the remaining sandbox restrictions.
  const permitsGameWindow = game.provider === "CQ9" && game.gameType === "3" && game.gameCode === "0";
  const failed = status === "error" || (status === "ready" && !url);
  const maximized = mode !== "normal";
  const clearNavigationGuard = useMobileNavigationGuard(true, fullBusy || exitDialog?.phase === "busy");
  const surface = () => playerRef.current?.closest<HTMLElement>(".modal__surface");
  const ownsFullscreen = () => Boolean(document.fullscreenElement && surface()?.contains(document.fullscreenElement));

  useEffect(() => {
    mounted.current = true;
    const currentSurface = surface();
    const changed = () => { if (!mounted.current) return; setMode(current => ownsFullscreen() ? "fullscreen" : current === "fullscreen" ? "normal" : current); setFullBusy(false); fullLock.current = false; };
    document.addEventListener("fullscreenchange", changed);
    return () => {
      mounted.current = false;
      exitRequestVersion.current += 1;
      exitRequest.current?.abort();
      document.removeEventListener("fullscreenchange", changed);
      if (document.fullscreenElement && currentSurface?.contains(document.fullscreenElement)) void document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  const exitView = useCallback(async () => {
    if (fullLock.current) return false;
    if (mode === "immersive") { setMode("normal"); setFullError(""); return true; }
    if (!document.fullscreenElement) { setMode("normal"); return true; }
    fullLock.current = true; setFullBusy(true); setFullError("");
    try { await document.exitFullscreen(); if (mounted.current) setMode("normal"); return true; }
    catch { if (mounted.current) setFullError("暂时无法退出全屏，可使用浏览器退出全屏操作后再返回大厅。"); return false; }
    finally { fullLock.current = false; if (mounted.current) setFullBusy(false); }
  }, [mode]);

  useEffect(() => {
    if (mode === "normal") return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault(); event.stopImmediatePropagation(); void exitView();
    };
    document.addEventListener("keydown", escape, true);
    return () => document.removeEventListener("keydown", escape, true);
  }, [mode, exitView]);

  async function enterView() {
    if (fullLock.current) return;
    const target = surface(); if (!target) return;
    setFullError("");
    if (!document.fullscreenEnabled || typeof target.requestFullscreen !== "function") { setMode("immersive"); return; }
    fullLock.current = true; setFullBusy(true);
    try {
      await target.requestFullscreen({ navigationUI: "hide" });
      if (!mounted.current) { if (document.fullscreenElement === target) await document.exitFullscreen(); return; }
      setMode(document.fullscreenElement ? "fullscreen" : "immersive");
    } catch { if (mounted.current) setMode("immersive"); }
    finally { fullLock.current = false; if (mounted.current) setFullBusy(false); }
  }

  const closeNow = useCallback(() => {
    const target = playerRef.current?.closest<HTMLElement>(".modal__surface");
    if (document.fullscreenElement && target?.contains(document.fullscreenElement)) void document.exitFullscreen?.().catch(() => {});
    onClose();
  }, [onClose]);

  const requestClose = useCallback(async () => {
    if (exitDialog || fullLock.current) return;
    exitTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (mode !== "normal" && !await exitView()) return;
    setHelpOpen(false);
    const saved = readGameExitReturnIntent(owner, game.provider);
    setExitDialog(saved
      ? { phase: "unknown", intent: saved, message: "上次退出时的转回结果尚未确认。请沿用原请求核对，避免重复转账。" }
      : { phase: "choice" });
  }, [exitDialog, exitView, game.provider, mode, owner]);

  useEffect(() => {
    if (!exitDialog) return;
    const frame = window.requestAnimationFrame(() => (exitFocusRef.current ?? exitPanelRef.current)?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [exitDialog?.phase]);

  useEffect(() => {
    if (browserExitRequested && !exitDialog) void requestClose();
  }, [browserExitRequested, exitDialog, requestClose]);

  const finishExit = useCallback((expired = false) => {
    setExitDialog(null);
    clearNavigationGuard();
    closeNow();
    if (expired) onSessionExpired();
  }, [clearNavigationGuard, closeNow, onSessionExpired]);

  function queueReturnAll(retryFresh = false) {
    if (exitDialog?.phase === "busy" || exitRequest.current) return;
    let intent = retryFresh ? undefined : exitDialog?.intent ?? readGameExitReturnIntent(owner, game.provider) ?? undefined;
    if (retryFresh && exitDialog?.intent) clearGameExitReturnIntent(exitDialog.intent);
    try {
      if (intent) persistGameExitReturnIntent(intent);
      else intent = createGameExitReturnIntent(owner, game.provider);
    } catch {
      setExitDialog({ phase: "failed", message: "无法保存本次转回的核对编号，因此没有发起余额查询或转账。请释放浏览器存储空间后重试，或直接退出。" });
      return;
    }
    // Rendering busy removes the supplier frame. The layout effect below is
    // the commit barrier: no balance read or transfer request starts before
    // React has applied that removal to the DOM.
    setExitDialog({ phase: "busy", intent });
  }

  async function submitReturnAll(intent: GameExitReturnIntent) {
    if (exitRequest.current) return;
    const version = ++exitRequestVersion.current;
    const controller = new AbortController();
    exitRequest.current = controller;
    try {
      const result = await api<WalletReturnAllResult>(`/api/user/wallet/providers/${encodeURIComponent(game.provider)}/return-all`, {
        method: "POST",
        body: JSON.stringify({ clientId: intent.clientId }),
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(25_000)]),
      });
      if (!mounted.current || version !== exitRequestVersion.current) return;
      if (!validReturnResult(result, owner, game.provider)) throw new Error("Invalid return-all response");
      if (result.result === "empty") {
        clearGameExitReturnIntent(intent);
        setExitDialog({ phase: "empty", amountCents: 0, message: result.message });
        return;
      }
      const transfer = result.transfer!;
      if (transfer.status === "succeeded") {
        clearGameExitReturnIntent(intent);
        const settlement = walletSettlementSummary(transfer);
        setExitDialog({
          phase: "success",
          amountCents: result.amountCents,
          actualAmountCents: settlement?.actualAmountCents ?? result.amountCents,
          transferId: transfer.id,
          message: settlement ? `上游实际转回 ¥ ${exitMoney.format(settlement.actualAmountCents / 100)}，未转回差额 ¥ ${exitMoney.format(settlement.differenceAmountCents / 100)}。` : result.message,
        });
        onBalanceReturned();
      } else if (transfer.status === "failed") {
        clearGameExitReturnIntent(intent);
        setExitDialog({ phase: "failed", amountCents: result.amountCents, transferId: transfer.id, message: transfer.message || result.message || "场馆未能完成本次转回。" });
      } else if (isDurableWalletReview(transfer)) {
        clearGameExitReturnIntent(intent);
        setExitDialog({ phase: "review", amountCents: result.amountCents, transferId: transfer.id, message: transfer.message || result.message || "该笔转回需要人工核查，系统已停止自动查询。" });
      } else {
        setExitDialog({ phase: "unknown", intent, amountCents: result.amountCents, transferId: transfer.id, message: transfer.message || result.message || "转回请求正在自动确认，请勿重复提交。" });
      }
    } catch (reason) {
      if (!mounted.current || version !== exitRequestVersion.current || controller.signal.aborted && reason instanceof DOMException && reason.name === "AbortError") return;
      if (reason instanceof ApiError) {
        if (reason.status === 401) {
          clearGameExitReturnIntent(intent);
          setExitDialog({ phase: "expired", message: "登录状态已失效，本次没有发起新的余额转回。退出后请重新登录核对场馆余额。" });
        } else if (reason.status === 409 && /待核对|未结案|处理中/.test(reason.message)) {
          clearGameExitReturnIntent(intent);
          setExitDialog({ phase: "blocked", message: reason.message });
        } else if ([400, 403, 404, 409, 422, 429].includes(reason.status)) {
          clearGameExitReturnIntent(intent);
          setExitDialog({ phase: "failed", message: reason.message || "暂时未能发起全部转回，请稍后重试。" });
        } else {
          setExitDialog({ phase: "unknown", intent, message: `${reason.message || "服务暂时无法确认处理结果。"} 原请求编号已保留，请继续核对，避免重复转账。` });
        }
      } else {
        setExitDialog({ phase: "unknown", intent, message: "请求结果尚未确认。原请求编号已保留，请继续核对，避免重复转账。" });
      }
    } finally {
      if (version === exitRequestVersion.current) exitRequest.current = null;
    }
  }

  useLayoutEffect(() => {
    if (exitDialog?.phase !== "busy" || !exitDialog.intent || exitRequest.current) return;
    if (playerRef.current?.querySelector("iframe")) {
      setExitDialog({ phase: "unknown", intent: exitDialog.intent, message: "游戏画面尚未安全关闭，未发起余额转回。原请求编号已保留，请继续核对。" });
      return;
    }
    void submitReturnAll(exitDialog.intent);
  }, [exitDialog?.phase, exitDialog?.intent?.clientId]);

  function closeExitDialog() {
    if (!exitDialog || exitDialog.phase === "busy") return;
    if (exitDialog.phase === "success" || exitDialog.phase === "empty") finishExit();
    else if (exitDialog.phase === "expired") finishExit(true);
    else if (exitDialog.phase === "choice") continueGame();
    else finishExit();
  }

  function continueGame() {
    setExitDialog(null);
    if (browserExitRequested) onBrowserExitCancel?.();
    const trigger = exitTriggerRef.current;
    window.requestAnimationFrame(() => {
      if (trigger?.isConnected) trigger.focus();
      else playerRef.current?.closest<HTMLElement>(".modal__surface")?.querySelector<HTMLElement>(".modal__header button")?.focus();
    });
  }

  // Reset before browser load events, including immediately cached game frames.
  useLayoutEffect(() => {
    setLoadedUrl(undefined);
    setSlow(false);
    setHelpOpen(false);
    if (status === "error") return;
    const timer = window.setTimeout(() => setSlow(true), 20_000);
    return () => window.clearTimeout(timer);
  }, [status, url]);

  const partialExit = exitDialog?.phase === "success" && exitDialog.actualAmountCents !== undefined && exitDialog.actualAmountCents < (exitDialog.amountCents ?? 0);
  const exitTitle = exitDialog?.phase === "success" ? partialExit ? "余额已部分转回" : "余额已转回" : exitDialog?.phase === "empty" ? "场馆余额为 0" : exitDialog?.phase === "failed" ? "暂时未能转回" : exitDialog?.phase === "blocked" ? "场馆转账待核对" : exitDialog?.phase === "review" ? "转回待人工核查" : exitDialog?.phase === "unknown" ? "转回结果待确认" : exitDialog?.phase === "expired" ? "登录状态已失效" : exitDialog ? "退出游戏" : game.name;
  const exitDescription = exitDialog ? `${game.provider} 场馆 · ${game.name}` : undefined;
  const exitCloseLabel = !exitDialog ? "返回大厅" : exitDialog.phase === "success" || exitDialog.phase === "empty" || exitDialog.phase === "failed" || exitDialog.phase === "blocked" || exitDialog.phase === "review" ? "退出游戏" : exitDialog.phase === "expired" ? "退出并重新登录" : exitDialog.phase === "unknown" ? "保留编号并退出" : "继续游戏";
  const keepGameFrame = !exitDialog || exitDialog.phase === "choice";

  return (
    <Modal open title={exitTitle} description={exitDescription} variant="game" closeLabel={exitCloseLabel} preventClose={exitDialog?.phase === "busy" || fullBusy} onClose={() => exitDialog ? closeExitDialog() : void requestClose()}>
      <div ref={playerRef} className={`game-player${maximized ? " game-player--maximized" : ""}`} data-display-mode={mode} inert={exitDialog ? true : undefined} aria-hidden={exitDialog ? "true" : undefined}>
        <div className="game-player__toolbar">
          <Button
            ref={helpTriggerRef}
            type="button"
            emphasis="ghost"
            aria-label="画面未显示？"
            aria-expanded={helpOpen}
            aria-controls={helpId}
            onClick={() => setHelpOpen((open) => !open)}
          >
            <CircleHelp aria-hidden="true" />{maximized ? "帮助" : "画面未显示？"}
          </Button>
          {!failed && (
            <Button type="button" emphasis="ghost" aria-label="重新连接" disabled={status === "connecting"} onClick={onRetry}>
              <RefreshCw aria-hidden="true" />{maximized ? "重连" : "重新连接"}
            </Button>
          )}
          <Button type="button" emphasis="ghost" busy={fullBusy} aria-pressed={mode !== "normal"} onClick={() => void (mode === "normal" ? enterView() : exitView())}>
            {mode === "normal" ? <Maximize aria-hidden="true" /> : <Minimize aria-hidden="true" />}{mode === "fullscreen" ? "退出全屏" : mode === "immersive" ? "退出沉浸" : "全屏"}
          </Button>
          {maximized && <Button type="button" emphasis="ghost" aria-label="返回大厅" onClick={() => void requestClose()}><ArrowLeft aria-hidden="true" />返回</Button>}
        </div>

        {mode === "immersive" && <p className="game-player__display-note" role="status">站内沉浸显示 · 浏览器地址栏仍由浏览器控制</p>}
        {hasFrame && permitsGameWindow && <p className="game-player__display-note">部分游戏会在新窗口打开，关闭该窗口可返回此大厅。</p>}
        {fullError && <p className="game-player__display-error" role="alert">{fullError}</p>}

        <div className="game-player__viewport">
          {hasFrame && keepGameFrame && (
            <iframe
              key={url}
              className="game-player__frame"
              title={`${game.name}游戏画面`}
              src={url}
              tabIndex={0}
              sandbox={`allow-scripts allow-same-origin allow-forms allow-pointer-lock allow-orientation-lock${permitsGameWindow ? " allow-popups" : ""}`}
              allow="autoplay"
              referrerPolicy="strict-origin-when-cross-origin"
              onLoad={() => setLoadedUrl(url)}
            />
          )}

          {!failed && (!hasFrame || !frameLoaded) && (
            <div className="game-player__state game-player__loading">
              <div className="game-player__state-content">
                <Spinner label={status === "connecting" ? "正在连接游戏" : "正在加载游戏画面"} />
                <p role="status">
                  {slow
                    ? status === "connecting"
                      ? "连接时间较长，可以先返回大厅，稍后再试。"
                      : "画面加载较慢，可尝试重新连接，或返回大厅稍后再试。"
                    : "请稍候，返回大厅可结束本次加载。"}
                </p>
              </div>
            </div>
          )}

          {failed && (
            <div className="game-player__state">
              <div className="game-player__state-content">
                <TriangleAlert className="game-player__error-icon" aria-hidden="true" />
                <h3>游戏暂时无法连接</h3>
                <p role="alert">{error || "暂未获得游戏地址，请重新连接或返回大厅稍后再试。"}</p>
                <div className="game-player__actions">
                  <Button type="button" onClick={onRetry}>重新连接</Button>
                  <Button type="button" emphasis="outline" intent="neutral" onClick={() => void requestClose()}>返回游戏大厅</Button>
                </div>
              </div>
            </div>
          )}

          <div id={helpId} className="game-player__help" hidden={!helpOpen}>
            <h3>画面一直没有显示？</h3>
            <p>如画面空白或提示无法显示，可尝试重新连接。仍未恢复时，请返回大厅联系客服，并告知游戏名称。</p>
            <p>部分游戏可能限制在本站内显示。</p>
            <p>需要放大画面时，请使用本站的全屏按钮。</p>
            <Button type="button" emphasis="outline" onClick={() => { setHelpOpen(false); helpTriggerRef.current?.focus(); }}>收起说明</Button>
          </div>
        </div>
      </div>
      {exitDialog && <div className={`game-exit-layer${exitDialog.phase === "choice" ? "" : " game-exit-layer--committed"}`}>
        <section ref={exitPanelRef} tabIndex={-1} className={`game-exit game-exit--${exitDialog.phase}`} aria-live="polite" aria-busy={exitDialog.phase === "busy" || undefined}>
          <span className="game-exit__mark" aria-hidden="true">
            {exitDialog.phase === "success" ? <CheckCircle2 /> : exitDialog.phase === "empty" ? <WalletCards /> : exitDialog.phase === "failed" || exitDialog.phase === "blocked" || exitDialog.phase === "review" || exitDialog.phase === "unknown" || exitDialog.phase === "expired" ? <TriangleAlert /> : <Landmark />}
          </span>
          {exitDialog.phase === "choice" && <>
            <h3>是否把场馆余额全部转回平台？</h3>
            <p>选择转回后，系统会先查询 {game.provider} 场馆当前余额，再将全部可用余额转回平台钱包。</p>
            <p className="game-exit__note">暂不转回会立即安全退出游戏，不会发起余额查询或资金转账。</p>
            <div className="modal-actions game-exit__actions">
              <Button ref={exitFocusRef} type="button" emphasis="outline" intent="neutral" onClick={continueGame}>继续游戏</Button>
              <Button type="button" emphasis="outline" intent="neutral" onClick={() => finishExit()}>暂不转回，退出</Button>
              <Button type="button" onClick={() => queueReturnAll()}>全部转回并退出</Button>
            </div>
          </>}
          {exitDialog.phase === "busy" && <>
            <h3>正在核对场馆余额</h3>
            <p role="status">正在查询并提交全部可用余额，请勿重复操作或关闭页面。</p>
            <div className="modal-actions game-exit__actions"><Button type="button" busy disabled>正在转回余额</Button></div>
          </>}
          {exitDialog.phase === "success" && <>
            <h3>{partialExit ? "已部分转回" : "已转回"} ¥ {exitMoney.format((exitDialog.actualAmountCents ?? exitDialog.amountCents ?? 0) / 100)}</h3>
            {partialExit && <dl className="game-exit__settlement" aria-label="部分转回明细"><div><dt>申请转回</dt><dd>¥ {exitMoney.format((exitDialog.amountCents ?? 0) / 100)}</dd></div><div><dt>实际转回</dt><dd>¥ {exitMoney.format((exitDialog.actualAmountCents ?? 0) / 100)}</dd></div><div><dt>未转回差额</dt><dd>¥ {exitMoney.format(((exitDialog.amountCents ?? 0) - (exitDialog.actualAmountCents ?? 0)) / 100)}</dd></div></dl>}
            <p role="status">{exitDialog.message || (partialExit ? "实际到账金额已计入平台钱包，差额未入账。" : "场馆余额已转回平台钱包。")}</p>
            {exitDialog.transferId && <p className="game-exit__reference">转账单号：{exitDialog.transferId}</p>}
            <div className="modal-actions game-exit__actions"><Button ref={exitFocusRef} type="button" onClick={() => finishExit()}>{partialExit ? "确认结果并退出" : "已转回，退出游戏"}</Button></div>
          </>}
          {exitDialog.phase === "empty" && <>
            <h3>没有需要转回的余额</h3>
            <p role="status">{exitDialog.message || `${game.provider} 场馆当前余额为 0，没有发起资金转账。`}</p>
            <div className="modal-actions game-exit__actions"><Button ref={exitFocusRef} type="button" onClick={() => finishExit()}>退出游戏</Button></div>
          </>}
          {exitDialog.phase === "failed" && <>
            <h3>余额未转回</h3>
            <p role="alert">{exitDialog.message || "暂时未能发起全部转回，请稍后重试。"}</p>
            {exitDialog.transferId && <p className="game-exit__reference">转账单号：{exitDialog.transferId}</p>}
            <div className="modal-actions game-exit__actions">
              <Button ref={exitFocusRef} type="button" emphasis="outline" intent="neutral" onClick={() => finishExit()}>直接退出</Button>
              <Button type="button" onClick={() => queueReturnAll(true)}>重新尝试转回</Button>
            </div>
          </>}
          {exitDialog.phase === "blocked" && <>
            <h3>先核对已有转账</h3>
            <p role="alert">{exitDialog.message}</p>
            <p>为避免重复划转，本次没有新建全部转回请求。请退出后到“我的钱包”核对原单。</p>
            <div className="modal-actions game-exit__actions"><Button ref={exitFocusRef} type="button" onClick={() => finishExit()}>退出游戏</Button></div>
          </>}
          {exitDialog.phase === "review" && <>
            <h3>该笔转回需要人工核查</h3>
            <p role="alert">{exitDialog.message}</p>
            {exitDialog.amountCents !== undefined && <p>申请转回：¥ {exitMoney.format(exitDialog.amountCents / 100)}</p>}
            {exitDialog.transferId && <p className="game-exit__reference">转账单号：{exitDialog.transferId}</p>}
            <p>系统已停止自动查询，也不会重复转账。请保留单号联系客服处理；该场馆在结案前保持锁定。</p>
            <div className="modal-actions game-exit__actions"><Button ref={exitFocusRef} type="button" onClick={() => finishExit()}>保留单号并退出</Button></div>
          </>}
          {exitDialog.phase === "unknown" && <>
            <h3>请先核对原请求</h3>
            <p role="alert">{exitDialog.message}</p>
            {exitDialog.amountCents !== undefined && <p>本次金额：¥ {exitMoney.format(exitDialog.amountCents / 100)}</p>}
            <p className="game-exit__reference">{exitDialog.transferId ? `转账单号：${exitDialog.transferId}` : `请求编号：${exitDialog.intent?.clientId}`}</p>
            <div className="modal-actions game-exit__actions">
              <Button ref={exitFocusRef} type="button" emphasis="outline" intent="neutral" onClick={() => finishExit()}>保留编号并退出</Button>
              <Button type="button" onClick={() => queueReturnAll()}>继续核对原请求</Button>
            </div>
          </>}
          {exitDialog.phase === "expired" && <>
            <h3>需要重新登录</h3>
            <p role="alert">{exitDialog.message}</p>
            <div className="modal-actions game-exit__actions"><Button ref={exitFocusRef} type="button" onClick={() => finishExit(true)}>退出并重新登录</Button></div>
          </>}
        </section>
      </div>}
    </Modal>
  );
}
